import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import './Dashboard.css';

const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [agentSummary, setAgentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.getAdminDashboard();
      const agentResponse = await reportsAPI.getAgentCollectionSummary();

      // Accept both {success, data} and direct data
      if (response && response.success) {
        setDashboard(response.data);
      } else if (response && response.data) {
        setDashboard(response.data);
      } else if (response) {
        setDashboard(response);
      } else {
        setDashboard(null);
      }

      if (agentResponse && agentResponse.success) {
        setAgentSummary(agentResponse.data);
      } else if (agentResponse && agentResponse.data) {
        setAgentSummary(agentResponse.data);
      } else if (agentResponse) {
        setAgentSummary(agentResponse);
      } else {
        setAgentSummary(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
      setDashboard(null);
      setAgentSummary(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        {error}
        <button className="button button-primary" onClick={fetchDashboard}>Retry</button>
      </div>
    );
  }

  if (!dashboard) {
    return <div className="dashboard-empty">No data available</div>;
  }

  const { summary, todayStats, topAgents } = dashboard;

  // Calculate agent-wise totals
  const agentTotals = agentSummary ? {
    todaysTarget: agentSummary.reduce((sum, a) => sum + (a.todays_collection_target || 0), 0),
    todaysActual: agentSummary.reduce((sum, a) => sum + (a.todays_collection_actual || 0), 0),
    pendingRecovery: agentSummary.reduce((sum, a) => sum + (a.pending_recovery || 0), 0),
  } : null;

  return (
    <div className="dashboard-container">
      <h1>Admin Dashboard</h1>

      {/* Summary Cards */}
      <div className="cards-grid">
        <div className="card">
          <div className="card-header">Total Agents</div>
          <div className="card-value">{summary.totalAgents}</div>
        </div>
        <div className="card">
          <div className="card-header">Total Retailers</div>
          <div className="card-value">{summary.totalRetailers}</div>
        </div>
        <div className="card">
          <div className="card-header">Total Loans</div>
          <div className="card-value">{summary.totalLoans}</div>
        </div>
        <div className="card">
          <div className="card-header">Active Loans</div>
          <div className="card-value">{summary.activeLoans}</div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="cards-grid">
        <div className="card">
          <div className="card-header">Active Loan Amount</div>
          <div className="card-value">₹{(summary.activeLoanAmount / 100000).toFixed(2)}L</div>
        </div>
        <div className="card">
          <div className="card-header">Total Invested</div>
          <div className="card-value">₹{(summary.totalInvested / 100000).toFixed(2)}L</div>
        </div>
        <div className="card">
          <div className="card-header">Total Collected</div>
          <div className="card-value">₹{(summary.totalCollected / 1000).toFixed(2)}K</div>
        </div>
        <div className="card">
          <div className="card-header">Current Balance</div>
          <div className="card-value">₹{(summary.currentBalance / 100000).toFixed(2)}L</div>
        </div>
      </div>

      {/* Loan Status Breakdown */}
      <div className="cards-grid">
        <div className="card">
          <div className="card-header">Pending Loans</div>
          <div className="card-value">{summary.pendingLoans}</div>
        </div>
        <div className="card">
          <div className="card-header">Completed Loans</div>
          <div className="card-value">{summary.completedLoans}</div>
        </div>
        <div className="card">
          <div className="card-header">Rejected Loans</div>
          <div className="card-value">{summary.rejectedLoans}</div>
        </div>
        <div className="card">
          <div className="card-header">Today's Collections</div>
          <div className="card-value">₹{(todayStats.amountCollected / 1000).toFixed(2)}K</div>
        </div>
      </div>

      {/* Agent Collection Metrics */}
      {agentTotals && (
        <>
          <h2 style={{ marginTop: '30px' }}>Agent Collection Metrics (Today)</h2>
          <div className="cards-grid">
            <div className="card">
              <div className="card-header">Today's Collection Target</div>
              <div className="card-value">₹{(agentTotals.todaysTarget / 1000).toFixed(2)}K</div>
            </div>
            <div className="card">
              <div className="card-header">Today's Collection (Actual)</div>
              <div className="card-value" style={{ color: '#27ae60' }}>
                ₹{(agentTotals.todaysActual / 1000).toFixed(2)}K
              </div>
            </div>
            <div className="card">
              <div className="card-header">Collection Efficiency</div>
              <div className="card-value">
                {agentTotals.todaysTarget > 0 
                  ? ((agentTotals.todaysActual / agentTotals.todaysTarget) * 100).toFixed(1) 
                  : 0}%
              </div>
            </div>
            <div className="card">
              <div className="card-header">Pending Recovery</div>
              <div className="card-value" style={{ color: '#e74c3c' }}>
                ₹{(agentTotals.pendingRecovery / 100000).toFixed(2)}L
              </div>
            </div>
          </div>
        </>
      )}

      {/* Top Agents */}
      <div className="section">
        <h2>Top Performing Agents</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Agent Name</th>
              <th>Loans</th>
              <th>Collections</th>
              <th>Retailers</th>
            </tr>
          </thead>
          <tbody>
            {topAgents && topAgents.map((agent) => (
              <tr key={agent.id}>
                <td>{agent.first_name} {agent.last_name}</td>
                <td>{agent.loan_count || 0}</td>
                <td>₹{(agent.total_collected / 1000).toFixed(2)}K</td>
                <td>{agent.retailer_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="refresh-button" onClick={fetchDashboard}>
        Refresh
      </button>
    </div>
  );
};

export default AdminDashboard;
